import React, { useState, useEffect, useRef } from 'react';

interface FilterOptions {
  status?: string;
  location?: string;
  sortBy?: string;
  search?: string;
}

interface NotesFilterProps {
  filters: FilterOptions;
  onChange: (filters: FilterOptions) => void;
  showStatusFilter?: boolean;
}

// Mock data for search suggestions
const SEARCH_SUGGESTIONS = [
  'encouragement',
  'inspiration',
  'motivation',
  'happiness',
  'gratitude',
  'kindness',
  'hope',
  'perseverance',
  'friendship',
  'love'
];

const NotesFilter: React.FC<NotesFilterProps> = ({
  filters,
  onChange,
  showStatusFilter = false
}) => {
  const [localFilters, setLocalFilters] = useState<FilterOptions>(filters);
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  
  // Update local filters when props change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);
  
  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Initialize from localStorage for search history
  useEffect(() => {
    const savedHistory = localStorage.getItem('notesSearchHistory');
    if (savedHistory) {
      try {
        setSearchHistory(JSON.parse(savedHistory));
      } catch (err) {
        console.error('Error parsing search history:', err);
      }
    }
  }, []);
  
  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Update local state
    setLocalFilters(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Pass changes to parent component
    onChange({
      ...localFilters,
      [name]: value
    });
  };
  
  // Handle search input (debounced)
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.trim().length > 1) {
      // Filter suggestions based on input
      const matchedSuggestions = [
        // First show matches from search history
        ...searchHistory.filter(item => 
          item.toLowerCase().includes(value.toLowerCase())
        ),
        // Then predefined suggestions
        ...SEARCH_SUGGESTIONS.filter(item => 
          item.toLowerCase().includes(value.toLowerCase()) && 
          !searchHistory.includes(item)
        )
      ].slice(0, 5); // Show at most 5 suggestions
      
      setSuggestions(matchedSuggestions);
      // Always show suggestions when we have matches for testing purposes
      setShowSuggestions(matchedSuggestions.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };
  
  // Handle search submission
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Only update if non-empty search term
    if (searchTerm.trim()) {
      // Save to search history if not already there
      if (!searchHistory.includes(searchTerm.trim())) {
        const newHistory = [searchTerm.trim(), ...searchHistory.slice(0, 9)];
        setSearchHistory(newHistory);
        localStorage.setItem('notesSearchHistory', JSON.stringify(newHistory));
      }
      
      setLocalFilters(prev => ({
        ...prev,
        search: searchTerm.trim()
      }));
      onChange({
        ...localFilters,
        search: searchTerm.trim()
      });
    } else {
      // Clear search filter if empty
      const updatedFilters = {
        ...localFilters,
        search: ''
      };
      setLocalFilters(updatedFilters);
      onChange(updatedFilters);
    }
    
    setShowSuggestions(false);
  };
  
  // Handle suggestion selection
  const handleSuggestionClick = (suggestion: string) => {
    setSearchTerm(suggestion);
    setShowSuggestions(false);
    
    // Save to search history if not already there
    if (!searchHistory.includes(suggestion)) {
      const newHistory = [suggestion, ...searchHistory.slice(0, 9)];
      setSearchHistory(newHistory);
      localStorage.setItem('notesSearchHistory', JSON.stringify(newHistory));
    }
    
    setLocalFilters(prev => ({
      ...prev,
      search: suggestion
    }));
    onChange({
      ...localFilters,
      search: suggestion
    });
  };
  
  // Clear all filters
  const clearFilters = () => {
    const resetFilters = {
      status: '',
      location: '',
      sortBy: 'newest',
      search: ''
    };

    setLocalFilters(resetFilters);
    onChange(resetFilters);
    setSearchTerm('');
  };
  
  // Toggle expanded filter view
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };
  
  // Highlight matching text in suggestions
  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return (
      <>
        {parts.map((part, i) => 
          regex.test(part) ? <mark key={i}>{part}</mark> : <span key={i}>{part}</span>
        )}
      </>
    );
  };
  
  return (
    <div className="notes-filter mb-4" data-testid="notes-filter">
      {/* Search Box */}
      <div className="search-box mb-3">
        <form onSubmit={handleSearchSubmit} className="d-flex position-relative">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Search notes..."
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => {
                // Show suggestions immediately on focus if we have some matches
                if (suggestions.length > 0) {
                  setShowSuggestions(true);
                }
                // Additionally, trigger search again to ensure we have suggestions
                if (searchTerm.trim().length > 1) {
                  handleSearchChange({ target: { value: searchTerm } } as React.ChangeEvent<HTMLInputElement>);
                }
              }}
              aria-label="Search notes"
              name="search"
            />
            <button
              className="btn btn-primary"
              type="submit"
              style={{
                backgroundColor: '#7FA99B',
                borderColor: '#7FA99B'
              }}
            >
              <i className="fas fa-search me-2"></i> Search
            </button>
          </div>
          
          {/* Search Suggestions */}
          {showSuggestions && (
            <div 
              className="search-suggestions position-absolute bg-white border rounded shadow-sm w-100 mt-1 z-index-dropdown"
              ref={suggestionsRef}
              style={{ top: '100%', zIndex: 1000 }}
              data-testid="search-suggestions"
            >
              <ul className="list-group list-group-flush">
                {suggestions.map((suggestion, index) => (
                  <li 
                    key={index} 
                    className="list-group-item list-group-item-action" 
                    onClick={() => handleSuggestionClick(suggestion)}
                    style={{ cursor: 'pointer' }}
                    data-testid={`suggestion-${index}`}
                  >
                    {highlightMatch(suggestion, searchTerm)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </form>
      </div>
      
      {/* Filter Controls Toggle */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={toggleExpanded}
            aria-expanded={isExpanded}
            data-testid="filter-toggle"
          >
            <i className={`fas fa-filter me-2${isExpanded ? '' : ''}`}></i>
            Filter Controls {isExpanded ? '▲' : '▼'}
          </button>
        </div>
        {(localFilters.status || localFilters.location) && (
          <div>
            <button 
              className="btn btn-link btn-sm text-danger"
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
      
      {/* Expanded Filter Controls */}
      {isExpanded && (
        <div className="filter-controls-expanded bg-light p-3 rounded mb-3">
          <div className="row g-3">
            <div className="col-md-4">
              <div className="form-group">
                <label htmlFor="sortBy" className="form-label">Sort By</label>
                <select
                  id="sortBy"
                  name="sortBy"
                  className="form-select"
                  value={localFilters.sortBy || 'newest'}
                  onChange={handleChange}
                >
                  <option value="createdAt_desc">Newest</option>
                  <option value="createdAt_asc">Oldest</option>
                  <option value="heart_count_desc">Most Hearted</option>
                  <option value="display.viewCount_desc">Most Viewed</option>
                  <option value="random">Random</option>
                </select>
              </div>
            </div>
            
            {showStatusFilter && (
              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="status" className="form-label">Status</label>
                  <select
                    id="status"
                    name="status"
                    className="form-select"
                    value={localFilters.status || ''}
                    onChange={handleChange}
                  >
                    <option value="">Any Status</option>
                    <option value="pending">Pending</option>
                    <option value="ai-approved">AI Approved</option>
                    <option value="moderator-approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
            )}
            
            <div className="col-md-4">
              <div className="form-group">
                <label htmlFor="location" className="form-label">Location</label>
                <input 
                  type="text"
                  id="location"
                  name="location"
                  className="form-control"
                  placeholder="City or state..."
                  value={localFilters.location || ''}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesFilter; 